/**
 * Sidebar navigation — grouped by OPERATIONS, CUSTOMERS, FINANCE, MARKETING & CMS, SYSTEM.
 * Menu `id` values must match `navigation/adminRoutes.js` and `security/acl.js`.
 */

export const SIDEBAR_SECTIONS = [
  {
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: 'fas fa-tachometer-alt',
        description: 'Overview and analytics',
      },
    ],
  },
  {
    sectionLabel: 'OPERATIONS',
    items: [
      {
        id: 'bookings-group',
        label: 'Bookings',
        icon: 'fas fa-calendar',
        description: 'Manage bookings',
        isGroup: true,
        badgeColor: 'bg-red-500',
        children: [
          { id: 'bookings', label: 'All Bookings', icon: 'fas fa-list' },
          { id: 'booking-activity', label: 'Booking Activity', icon: 'fas fa-stream' },
          { id: 'booking-manual-broadcast', label: 'Manual Broadcast', icon: 'fas fa-broadcast-tower' },
          { id: 'guest-vendor-interests', label: 'Guest Vendor Interests', icon: 'fas fa-users' },
          { id: 'vendor-cab-availability', label: 'Vendor Cab Availability', icon: 'fas fa-car-side' },
          { id: 'booking-leads', label: 'User Journey Funnels', icon: 'fas fa-user-clock' },
          { id: 'booking-quotes', label: 'Quotes', icon: 'fas fa-file-invoice-dollar' },
          { id: 'broadcasts', label: 'Broadcasts', icon: 'fas fa-broadcast-tower' },
        ],
      },
      {
        id: 'cabs-group',
        label: 'Fleet',
        icon: 'fas fa-car',
        description: 'Fleet vehicle management',
        isGroup: true,
        children: [
          { id: 'cabs-fleet', label: 'Fleet', icon: 'fas fa-th-list' },
          { id: 'cabs-approval', label: 'Cabs Approval', icon: 'fas fa-clipboard-check' },
          { id: 'segment-configuration', label: 'Cab segments', icon: 'fas fa-taxi' },
          { id: 'cab-expenses', label: 'Cab expenses', icon: 'fas fa-receipt' },
          { id: 'cab-revenue', label: 'Cab revenue', icon: 'fas fa-chart-pie' },
          { id: 'cab-sold-out', label: 'Sold Out', icon: 'fas fa-ban' },
        ],
      },
      {
        id: 'drivers-group',
        label: 'Drivers',
        icon: 'fas fa-user',
        description: 'Driver management',
        isGroup: true,
        children: [
          { id: 'drivers-roster', label: 'Drivers', icon: 'fas fa-users' },
          { id: 'driver-approval', label: 'Driver Approval', icon: 'fas fa-clipboard-check' },
        ],
      },
      {
        id: 'vendors-group',
        label: 'Vendors',
        icon: 'fas fa-user-circle',
        description: 'Vendor management',
        isGroup: true,
        children: [
          { id: 'vendors', label: 'Vendors', icon: 'fas fa-users' },
          { id: 'vendor-join-us-enquiries', label: 'Join Us Enquiry', icon: 'fas fa-handshake' },
          { id: 'pan-india-vendors', label: 'Pan India Vendors', icon: 'fas fa-map-marked-alt' },
          { id: 'vendor-onboarding-campaigns', label: 'Onboarding Campaigns', icon: 'fas fa-envelope-open-text' },
          { id: 'vendor-onboarding-form', label: 'Onboarding Form', icon: 'fas fa-clipboard-list' },
          { id: 'vendor-notifications', label: 'Vendor notifications', icon: 'fas fa-bell' },
          { id: 'vendor-penalties', label: 'Vendor penalties', icon: 'fas fa-gavel' },
          {
            id: 'dmc-center-group',
            label: 'DMC Center',
            icon: 'fas fa-handshake',
            isGroup: true,
            children: [
              { id: 'dmc-package-prospects', label: 'Package DMC', icon: 'fas fa-suitcase' },
              { id: 'dmc-cab-prospects', label: 'Cab DMC', icon: 'fas fa-car-side' },
            ],
          },
        ],
      },

      {
        id: 'watchtower',
        label: 'Trips / Live Tracking',
        icon: 'fas fa-crosshairs',
        description: 'Realtime fleet monitoring',
      },
      {
        id: 'pricing-group',
        label: 'Pricing',
        icon: 'fas fa-tags',
        isGroup: true,
        children: [
          { id: 'pricing', label: 'Tariff Management', icon: 'fas fa-sliders-h' },
          { id: 'pricing-price-analysis', label: 'Price Analysis', icon: 'fas fa-chart-line' },
          { id: 'pricing-configuration', label: 'Pricing Configuration', icon: 'fas fa-cog' },
        ],
      },
      {
        id: 'tours-packages-group',
        label: 'Tours & packages',
        icon: 'fas fa-suitcase-rolling',
        isGroup: true,
        children: [
          { id: 'cab-tour-packages', label: 'Cab tour packages', icon: 'fas fa-map-signs' },
          { id: 'jyotirlinga-engine', label: 'Jyotirlinga engine', icon: 'fas fa-om' },
          { id: 'ashtavinayak-tours', label: 'Ashtavinayak tours', icon: 'fas fa-route' },
          { id: 'ashtavinayak-temples', label: 'Ashtavinayak temples', icon: 'fas fa-landmark' },
          { id: 'pilgrimage-sites', label: 'Pilgrimage sites', icon: 'fas fa-place-of-worship' },
          { id: 'cab-package-enquiries', label: 'Enquiries', icon: 'fas fa-envelope-open-text' },
          { id: 'nearby-places', label: 'Nearby places to visit', icon: 'fas fa-map-marker-alt' },
          { id: 'cab-package-user-logged', label: 'User Logged', icon: 'fas fa-user-clock' },
          { id: 'tours-packages-settings', label: 'Settings', icon: 'fas fa-cog' },
        ],
      },
      {
        id: 'corporate-cms-group',
        label: 'Corporate Landing',
        icon: 'fas fa-briefcase',
        isGroup: true,
        children: [
          { id: 'corporate-cms-hub', label: 'Hub & SEO', icon: 'fas fa-globe' },
          { id: 'corporate-cms-cities', label: 'Cities', icon: 'fas fa-city' },
          { id: 'corporate-cms-services', label: 'Services', icon: 'fas fa-concierge-bell' },
          { id: 'corporate-cms-packages', label: 'Packages', icon: 'fas fa-box-open' },
          { id: 'corporate-cms-enquiries', label: 'Enquiries', icon: 'fas fa-envelope-open-text' },
          { id: 'corporate-cms-localities', label: 'Localities', icon: 'fas fa-map-marker-alt' },
        ],
      },
      {
        id: 'luxury-cars-group',
        label: 'Luxury Cars',
        icon: 'fas fa-gem',
        isGroup: true,
        children: [
          { id: 'luxury-cars-dashboard', label: 'Dashboard', icon: 'fas fa-chart-line' },
          { id: 'luxury-cars-cities', label: 'Cities', icon: 'fas fa-city' },
          { id: 'luxury-cars-fleet', label: 'Luxury Cars', icon: 'fas fa-car-side' },
          { id: 'luxury-cars-tariffs', label: 'Tariffs', icon: 'fas fa-tags' },
          { id: 'luxury-cars-packages', label: 'Packages', icon: 'fas fa-box-open' },
          { id: 'luxury-cars-pages', label: 'Pages', icon: 'fas fa-file-alt' },
          {
            id: 'luxury-cars-faqs-group',
            label: 'All FAQs',
            icon: 'fas fa-question-circle',
            isGroup: true,
            children: [
              { id: 'luxury-cars-faqs', label: 'All FAQs', icon: 'fas fa-list' },
              { id: 'luxury-cars-faq-categories', label: 'FAQ Categories', icon: 'fas fa-folder-open' },
              { id: 'luxury-cars-faq-import', label: 'FAQ Bulk Import', icon: 'fas fa-file-import' },
              { id: 'luxury-cars-faq-templates', label: 'FAQ Templates', icon: 'fas fa-clone' },
              { id: 'luxury-cars-faq-analytics', label: 'FAQ Analytics', icon: 'fas fa-chart-bar' },
            ],
          },
          { id: 'luxury-cars-enquiries', label: 'Enquiry', icon: 'fas fa-envelope' },
          { id: 'luxury-cars-bookings', label: 'Bookings', icon: 'fas fa-calendar-check' },
          { id: 'luxury-cars-vendors', label: 'Vendors', icon: 'fas fa-handshake' },
          { id: 'luxury-cars-pricing', label: 'Customer Pricing', icon: 'fas fa-calculator' },
          { id: 'luxury-cars-welcome-goodies', label: 'Welcome Goodies', icon: 'fas fa-gift' },
          { id: 'luxury-cars-terms', label: 'Important Terms', icon: 'fas fa-balance-scale' },
          { id: 'luxury-cars-settings', label: 'Settings', icon: 'fas fa-cog' },
          { id: 'luxury-cars-activities', label: 'Activities', icon: 'fas fa-history' },
        ],
      },
      {
        id: 'city-group',
        label: 'Cities & routes',
        icon: 'fas fa-map-marked-alt',
        isGroup: true,
        children: [
          { id: 'cities', label: 'Cities', icon: 'fas fa-city' },
          { id: 'location-missing-searches', label: 'Missing location searches', icon: 'fas fa-search-minus' },
          { id: 'location-search-index', label: 'Location search index', icon: 'fas fa-database' },
          { id: 'location-master-ops', label: 'Location master ops', icon: 'fas fa-sync-alt' },
          { id: 'routes', label: 'Tariff & Routes', icon: 'fas fa-route' },
          { id: 'route-page-user-logged', label: 'Route User Logged', icon: 'fas fa-user-clock' },
          { id: 'vehicle-landing-user-logged', label: 'Vehicle landing User Logged', icon: 'fas fa-car' },
          { id: 'distance-calculator', label: 'Distance calculator', icon: 'fas fa-ruler-horizontal' },
          { id: 'routes-settings', label: 'Routes Setting', icon: 'fas fa-sliders-h' },
        ],
      },
    ],
  },
  {
    sectionLabel: 'CUSTOMERS',
    items: [
      {
        id: 'customers-group',
        label: 'Customers',
        icon: 'fas fa-users',
        isGroup: true,
        children: [
          { id: 'users', label: 'All customers', icon: 'fas fa-list' },
          { id: 'customer-activities-log', label: 'Activities Log', icon: 'fas fa-history' },
        ],
      },
      {
        id: 'corporate-group',
        label: 'Corporate',
        icon: 'fas fa-building',
        isGroup: true,
        children: [
          { id: 'corporate-clients', label: 'Corporate clients', icon: 'fas fa-user-tie' },
          { id: 'corporate-pending', label: 'Pending approvals', icon: 'fas fa-hourglass-half' },
          { id: 'corporate-bookings', label: 'Corporate bookings', icon: 'fas fa-briefcase' },
          { id: 'corporate-pricing', label: 'Corporate pricing', icon: 'fas fa-tags' },
          { id: 'corporate-contracts', label: 'Contracts', icon: 'fas fa-file-contract' },
          { id: 'corporate-invoices', label: 'Invoices', icon: 'fas fa-file-invoice' },
          { id: 'corporate-reports', label: 'Reports', icon: 'fas fa-chart-bar' },
          { id: 'corporate-erp', label: 'ERP integrations', icon: 'fas fa-plug' },
        ],
      },
      {
        id: 'reviews-group',
        label: 'Reviews',
        icon: 'fas fa-star',
        description: 'Post-trip review automation and testimonials',
        isGroup: true,
        children: [
          { id: 'review-automation-dashboard', label: 'Dashboard', icon: 'fas fa-chart-line' },
          { id: 'review-automation-requests', label: 'Requests', icon: 'fas fa-inbox' },
          { id: 'review-automation-reports', label: 'Reports', icon: 'fas fa-chart-bar' },
          { id: 'review-automation-settings', label: 'Settings', icon: 'fas fa-cog' },
          { id: 'reviews', label: 'Testimonials', icon: 'fas fa-comment' },
        ],
      },
      {
        id: 'support-tickets',
        label: 'Support tickets',
        icon: 'fas fa-comment-dots',
        description: 'Help desk tickets',
      },
      {
        id: 'contact-group',
        label: 'Contact page',
        icon: 'fas fa-address-card',
        isGroup: true,
        children: [
          { id: 'contact-messages', label: 'Messages', icon: 'fas fa-envelope-open-text' },
          { id: 'contact-form-settings', label: 'Settings', icon: 'fas fa-cog' },
        ],
      },
      {
        id: 'crm-group',
        label: 'CRM & support tools',
        icon: 'fas fa-headset',
        isGroup: true,
        children: [
          { id: 'crm-call-logs', label: 'Call logs', icon: 'fas fa-phone' },
          { id: 'crm-complaints', label: 'Complaints', icon: 'fas fa-comment-dots' },
          { id: 'crm-refund-tickets', label: 'Refund tickets', icon: 'fas fa-undo' },
          { id: 'crm-escalation', label: 'Escalation matrix', icon: 'fas fa-level-up-alt' },
          { id: 'crm-whatsapp', label: 'WhatsApp', icon: 'fab fa-whatsapp' },
        ],
      },
    ],
  },
  {
    sectionLabel: 'FINANCE',
    items: [
      {
        id: 'finance-invoices-group',
        label: 'Invoices',
        icon: 'fas fa-file-invoice',
        isGroup: true,
        children: [
          { id: 'booking-invoices', label: 'Booking invoices', icon: 'fas fa-file-invoice' },
          { id: 'finance-gst-invoices', label: 'GST invoices', icon: 'fas fa-file-invoice-dollar' },
          { id: 'booking-transactions', label: 'Transactions', icon: 'fas fa-sync-alt' },
        ],
      },
      {
        id: 'finance-customer-payments',
        label: 'Payments',
        icon: 'fas fa-hand-holding-usd',
        description: 'Customer payments and dues',
      },
      {
        id: 'finance-vendor-settlement-group',
        label: 'Vendor settlement',
        icon: 'fas fa-store',
        isGroup: true,
        children: [
          { id: 'finance-vendor-settlement', label: 'Vendor settlement', icon: 'fas fa-hand-holding-usd' },
          { id: 'finance-vendor-payouts', label: 'Vendor payouts', icon: 'fas fa-money-check-alt' },
          { id: 'finance-vendor-finance', label: 'Vendor finance', icon: 'fas fa-store' },
          { id: 'finance-multi-vendor', label: 'Multi-vendor accounting', icon: 'fas fa-sitemap' },
          { id: 'finance-reconciliation', label: 'Reconciliation', icon: 'fas fa-balance-scale' },
        ],
      },
      {
        id: 'finance-driver-ledger',
        label: 'Driver settlement',
        icon: 'fas fa-university',
        description: 'Driver settlements and payroll',
      },
      {
        id: 'finance-expenses',
        label: 'Expenses',
        icon: 'fas fa-money-bill-wave',
        description: 'Fuel, toll, maintenance',
      },
      {
        id: 'finance-reports-group',
        label: 'Reports',
        icon: 'fas fa-chart-bar',
        isGroup: true,
        children: [
          { id: 'finance-dashboard', label: 'Finance dashboard', icon: 'fas fa-chart-pie' },
          { id: 'analytics', label: 'Analytics reports', icon: 'fas fa-chart-line' },
          { id: 'finance-vehicle-pl', label: 'Vehicle income', icon: 'fas fa-car-side' },
          { id: 'finance-fleet-profitability', label: 'Fleet profitability', icon: 'fas fa-chart-line' },
          { id: 'finance-gst-summary', label: 'GST summary', icon: 'fas fa-file-invoice-dollar' },
          { id: 'finance-vehicle-ledger', label: 'Vehicle ledger', icon: 'fas fa-book' },
          { id: 'finance-driver-settlements', label: 'Driver settlement report', icon: 'fas fa-list-alt' },
          { id: 'finance-fleet-utilization', label: 'Fleet utilization', icon: 'fas fa-percentage' },
          { id: 'finance-driver-performance', label: 'Driver performance', icon: 'fas fa-star' },
          { id: 'finance-route-pl', label: 'Route P&L', icon: 'fas fa-route' },
          { id: 'finance-emi-loans', label: 'EMI / loans', icon: 'fas fa-landmark' },
          { id: 'finance-toll-fuel', label: 'Toll & fuel', icon: 'fas fa-gas-pump' },
          { id: 'finance-fastag-import', label: 'FASTag import', icon: 'fas fa-road' },
          { id: 'finance-zoho-export', label: 'Zoho export', icon: 'fas fa-cloud-download-alt' },
          { id: 'finance-checkout-addons-report', label: 'Checkout Add-ons Report', icon: 'fas fa-puzzle-piece' },
          { id: 'finance-snackbox-orders-report', label: 'Snackbox Ordered Report', icon: 'fas fa-box-open' },
        ],
      },
    ],
  },
  {
    sectionLabel: 'MARKETING & CMS',
    items: [
      {
        id: 'business-presence-group',
        label: 'Business presence',
        icon: 'fas fa-store',
        isGroup: true,
        children: [
          { id: 'business-presence-dashboard', label: 'Dashboard', icon: 'fas fa-chart-pie' },
          { id: 'business-presence-locations', label: 'Locations', icon: 'fas fa-map-marker-alt' },
          { id: 'business-presence-posts', label: 'Posts & calendar', icon: 'fas fa-calendar-alt' },
          { id: 'business-presence-reviews', label: 'Review inbox', icon: 'fas fa-star' },
          { id: 'business-presence-accounts', label: 'Connected accounts', icon: 'fas fa-plug' },
          { id: 'business-presence-seo', label: 'Local SEO', icon: 'fas fa-search' },
          { id: 'business-presence-settings', label: 'AI & publishing', icon: 'fas fa-sliders-h' },
        ],
      },
      {
        id: 'marketing-group',
        label: 'Marketing',
        icon: 'fas fa-bullhorn',
        isGroup: true,
        children: [
          { id: 'google-ads-dashboard', label: 'Marketing Dashboard', icon: 'fas fa-tachometer-alt' },
          { id: 'google-ads-campaigns', label: 'Google Ads Campaigns', icon: 'fas fa-bullhorn' },
          { id: 'google-ads-keywords', label: 'Keywords & Search Terms', icon: 'fas fa-key' },
          { id: 'google-ads-landing-pages', label: 'Landing Pages', icon: 'fas fa-file-alt' },
          { id: 'google-ads-attribution', label: 'Booking Attribution', icon: 'fas fa-link' },
          { id: 'google-ads-conversion-tracking', label: 'Conversion Tracking', icon: 'fas fa-bullseye' },
          { id: 'google-ads-spend-import', label: 'Advertising Cost Import', icon: 'fas fa-file-csv' },
          { id: 'google-ads-reports', label: 'Reports', icon: 'fas fa-file-export' },
          { id: 'google-ads-integrations', label: 'Google Integrations', icon: 'fas fa-plug' },
          { id: 'google-ads-settings-audit', label: 'Settings & Audit Logs', icon: 'fas fa-clipboard-list' },
          { id: 'marketing-leads', label: 'User Journey Funnels', icon: 'fas fa-user-clock' },
          { id: 'utm-tracking-dashboard', label: 'UTM Tracking', icon: 'fas fa-chart-line' },
          { id: 'utm-tracking-urls', label: 'Tracked URLs', icon: 'fas fa-list' },
          { id: 'marketing-campaigns', label: 'UTM Campaign Performance', icon: 'fas fa-chart-bar' },
          { id: 'marketing-sources', label: 'Source Analytics', icon: 'fas fa-share-alt' },
          { id: 'marketing-conversion', label: 'Conversion Analytics', icon: 'fas fa-filter' },
          { id: 'marketing-revenue', label: 'Revenue Attribution', icon: 'fas fa-rupee-sign' },
          { id: 'newsletter-subscribers', label: 'Newsletter · Subscribers', icon: 'fas fa-users' },
          { id: 'newsletter-campaigns', label: 'Newsletter · Campaigns', icon: 'fas fa-paper-plane' },
          { id: 'newsletter-settings', label: 'Newsletter · Settings', icon: 'fas fa-cog' },
        ],
      },
      {
        id: 'brand-ads-group',
        label: 'Brand Ads',
        icon: 'fas fa-ad',
        isGroup: true,
        children: [
          { id: 'brand-ads-dashboard', label: 'Dashboard', icon: 'fas fa-chart-pie' },
          { id: 'brand-ads-campaigns', label: 'Campaigns', icon: 'fas fa-bullhorn' },
          { id: 'brand-ads-advertisers', label: 'Advertisers', icon: 'fas fa-building' },
          { id: 'brand-ads-placements', label: 'Placements', icon: 'fas fa-th-large' },
          { id: 'brand-ads-landing', label: 'Landing Pages', icon: 'fas fa-file-image' },
          { id: 'brand-ads-reports', label: 'Reports', icon: 'fas fa-file-excel' },
          { id: 'brand-ads-settings', label: 'AdSense Settings', icon: 'fas fa-cog' },
        ],
      },
      {
        id: 'ai-visibility-group',
        label: 'AI Search Visibility',
        icon: 'fas fa-robot',
        isGroup: true,
        children: [
          { id: 'ai-visibility-dashboard', label: 'AI Visibility Dashboard', icon: 'fas fa-tachometer-alt' },
          { id: 'ai-visibility-urls', label: 'Public URL Inventory', icon: 'fas fa-list' },
          { id: 'ai-visibility-crawlers', label: 'Crawler Access', icon: 'fas fa-spider' },
          { id: 'ai-visibility-keywords', label: 'Keyword Intelligence', icon: 'fas fa-key' },
          { id: 'ai-visibility-opportunities', label: 'Content Opportunities', icon: 'fas fa-lightbulb' },
          { id: 'ai-visibility-structured-data', label: 'Structured Data Audit', icon: 'fas fa-code' },
          { id: 'ai-visibility-tests', label: 'AI Search Tests', icon: 'fas fa-vial' },
          { id: 'ai-visibility-referrals', label: 'Referrals and Conversions', icon: 'fas fa-chart-line' },
          { id: 'ai-visibility-reports', label: 'Reports', icon: 'fas fa-file-export' },
          { id: 'ai-visibility-seo-audit', label: 'Public SEO URL Audit', icon: 'fas fa-clipboard-check' },
          { id: 'ai-visibility-settings', label: 'Settings', icon: 'fas fa-cog' },
        ],
      },
      {
        id: 'seo-group',
        label: 'SEO',
        icon: 'fas fa-search',
        isGroup: true,
        children: [
          {
            id: 'seo-control-center',
            label: 'Control Center',
            icon: 'fas fa-tachometer-alt',
            description: 'Site-wide SEO health, modules, and quick links',
          },
          {
            id: 'seo-page-urls',
            label: 'Page URLs',
            icon: 'fas fa-list',
            description: 'All frontend URLs, titles, and keywords',
          },
          {
            id: 'seo-score-dashboard',
            label: 'SEO Score Dashboard',
            icon: 'fas fa-tachometer-alt',
            description: 'Phase 1 hubs scored with §23 weights (no fake GSC)',
          },
          {
            id: 'locality-landing-cms',
            label: 'Locality Landings',
            icon: 'fas fa-map-marker-alt',
            description: 'Demand-gated /locations CMS — index only with unique content',
          },
          {
            id: 'city-money-pages',
            label: 'City Money Pages',
            icon: 'fas fa-city',
            description: 'City cab-booking hubs: Nashik, Aurangabad, Nagpur, Ahmedabad, Surat',
          },
          {
            id: 'route-pages',
            label: 'Route pages',
            icon: 'fas fa-globe',
            description: 'SEO route analytics',
          },
          {
            id: 'sitemaps',
            label: 'Sitemap Generator',
            icon: 'fas fa-sitemap',
            description: 'XML sitemap generation and download',
          },
          {
            id: 'robots-txt',
            label: 'Robots.txt Generator',
            icon: 'fas fa-robot',
            description: 'Crawler access rules and robots.txt publishing',
          },
          {
            id: 'seo-settings',
            label: 'SEO Settings',
            icon: 'fas fa-sliders-h',
            description: 'Default meta tags and analytics',
          },
        ],
      },
      {
        id: 'seo-engine-group',
        label: 'SEO Management',
        icon: 'fas fa-map-marked-alt',
        isGroup: true,
        children: [
          { id: 'seo-engine-dashboard', label: 'SEO Dashboard', icon: 'fas fa-tachometer-alt' },
          { id: 'seo-engine-cities', label: 'Cities', icon: 'fas fa-city' },
          { id: 'seo-engine-states', label: 'States', icon: 'fas fa-flag' },
          { id: 'seo-engine-localities', label: 'Localities', icon: 'fas fa-map-pin' },
          { id: 'seo-engine-routes', label: 'Routes', icon: 'fas fa-route' },
          { id: 'seo-engine-services', label: 'Services', icon: 'fas fa-concierge-bell' },
          { id: 'seo-engine-keywords', label: 'Keywords', icon: 'fas fa-key' },
          { id: 'seo-engine-pages', label: 'Landing Pages', icon: 'fas fa-file-alt' },
          { id: 'seo-engine-templates', label: 'SEO Templates', icon: 'fas fa-copy' },
          { id: 'seo-engine-blocks', label: 'Content Blocks', icon: 'fas fa-th-large' },
          { id: 'seo-engine-faqs', label: 'FAQs', icon: 'fas fa-question-circle' },
          { id: 'seo-engine-redirects', label: 'Redirects', icon: 'fas fa-random' },
          { id: 'seo-engine-sitemap', label: 'Sitemap', icon: 'fas fa-sitemap' },
          { id: 'seo-engine-audit', label: 'SEO Audit', icon: 'fas fa-clipboard-check' },
          { id: 'seo-engine-internal-links', label: 'Internal Links', icon: 'fas fa-link' },
          { id: 'seo-engine-schema', label: 'Schema Management', icon: 'fas fa-code' },
        ],
      },
      {
        id: 'ai-seo-group',
        label: 'AI SEO',
        icon: 'fas fa-robot',
        isGroup: true,
        children: [
          { id: 'ai-seo-overview', label: 'Overview', icon: 'fas fa-tachometer-alt', description: 'Pipeline health, budget, first-run checklist' },
          { id: 'ai-seo-how-to', label: 'How to use', icon: 'fas fa-book-open', description: 'End-to-end staff flow: settings, keywords, approve, publish' },
          { id: 'ai-seo-providers', label: 'Providers', icon: 'fas fa-plug', description: 'NVIDIA, Gemini, OpenRouter, Groq, Ollama' },
          { id: 'ai-seo-models', label: 'Models', icon: 'fas fa-microchip', description: 'Discover, test, mark free vs paid' },
          { id: 'ai-seo-routing', label: 'Routing & fallback', icon: 'fas fa-random', description: 'Task → primary / fallback models' },
          { id: 'ai-seo-settings', label: 'AI settings', icon: 'fas fa-sliders-h', description: 'Top-keyword blogs, approval, social, budget' },
          { id: 'ai-seo-prompts', label: 'Prompts', icon: 'fas fa-comment-dots' },
          { id: 'ai-seo-keywords', label: 'Keywords', icon: 'fas fa-key' },
          { id: 'ai-seo-content', label: 'Content', icon: 'fas fa-file-alt' },
          { id: 'ai-seo-calendar', label: 'Calendar', icon: 'fas fa-calendar-alt' },
          { id: 'ai-seo-social', label: 'Social', icon: 'fas fa-share-alt' },
          { id: 'ai-seo-knowledge', label: 'Knowledge base', icon: 'fas fa-book' },
          { id: 'ai-seo-usage', label: 'Usage', icon: 'fas fa-chart-line' },
        ],
      },
      {
        id: 'analytics-coupon-usage',
        label: 'Coupons & offers',
        icon: 'fas fa-tag',
        description: 'Coupon usage report',
      },
      {
        id: 'content-group',
        label: 'Blog & content',
        icon: 'fas fa-file-alt',
        isGroup: true,
        children: [
          { id: 'cms-route-pages', label: 'CMS pages', icon: 'fas fa-file-code' },
          { id: 'cms-route-pages-css', label: 'CMS styles', icon: 'fas fa-paint-brush' },
          { id: 'ai-route-manager', label: 'AI route manager', icon: 'fas fa-brain' },
          { id: 'blog-seo-dashboard', label: 'Content dashboard', icon: 'fas fa-chart-pie' },
          { id: 'blog-posts', label: 'Blog posts', icon: 'fas fa-newspaper' },
          { id: 'blog-calendar', label: 'Content calendar', icon: 'fas fa-calendar-alt' },
          { id: 'blog-keywords', label: 'Keywords', icon: 'fas fa-key' },
          { id: 'blog-clusters', label: 'Clusters', icon: 'fas fa-project-diagram' },
          { id: 'blog-comments', label: 'Blog comments', icon: 'fas fa-comments' },
          { id: 'blog-categories', label: 'Blog categories', icon: 'fas fa-folder' },
          { id: 'blog-authors', label: 'Authors', icon: 'fas fa-user-edit' },
          { id: 'blog-internal-links', label: 'Internal links', icon: 'fas fa-link' },
          { id: 'blog-seo-audit', label: 'SEO audit', icon: 'fas fa-clipboard-check' },
          { id: 'blog-cannibalization', label: 'Cannibalization', icon: 'fas fa-exclamation-triangle' },
          { id: 'blog-redirects', label: 'Redirects', icon: 'fas fa-random' },
          { id: 'blog-tags', label: 'Blog tags', icon: 'fas fa-tags' },
        ],
      },
      {
        id: 'homepage-group',
        label: 'HomePage',
        icon: 'fas fa-home',
        isGroup: true,
        children: [
          { id: 'homepage-header', label: 'Header', icon: 'fas fa-heading' },
          { id: 'homepage-hero-banner', label: 'Hero Banner', icon: 'fas fa-image' },
          { id: 'homepage-footer', label: 'Footer columns & mega tabs', icon: 'fas fa-shoe-prints' },
          { id: 'homepage-not-found', label: '404 pages', icon: 'fas fa-exclamation-triangle' },
          { id: 'homepage-sections', label: 'Sections', icon: 'fas fa-layer-group' },
          { id: 'gallery-share-settings', label: 'Gallery · Share', icon: 'fas fa-share-alt' },
          { id: 'homepage-popular-routes', label: 'Popular routes', icon: 'fas fa-map-marked-alt' },
          { id: 'homepage-popular-route-categories', label: 'Route categories', icon: 'fas fa-tags' },
        ],
      },
      {
        id: 'faq-group',
        label: 'FAQ',
        icon: 'fas fa-circle-question',
        isGroup: true,
        children: [
          { id: 'faqs', label: 'FAQs', icon: 'fas fa-circle-question' },
          { id: 'faq-categories', label: 'FAQ categories', icon: 'fas fa-folder-open' },
        ],
      },
      {
        id: 'checkout-group',
        label: 'Checkout',
        icon: 'fas fa-shopping-cart',
        isGroup: true,
        children: [
          { id: 'checkout-addons', label: 'Add-ons', icon: 'fas fa-puzzle-piece' },
          {
            id: 'snack-box-management-group',
            label: 'Snack Box Management',
            icon: 'fas fa-box-open',
            isGroup: true,
            children: [
              { id: 'snack-box-dashboard', label: 'Dashboard', icon: 'fas fa-chart-line' },
              { id: 'snack-boxes', label: 'Products', icon: 'fas fa-box-open' },
              { id: 'snack-box-inventory', label: 'Inventory', icon: 'fas fa-warehouse' },
              { id: 'snack-box-orders', label: 'Orders', icon: 'fas fa-receipt' },
              { id: 'snack-box-reports', label: 'Reports', icon: 'fas fa-chart-bar' },
              { id: 'snack-box-settings', label: 'Settings', icon: 'fas fa-cog' },
            ],
          },
          { id: 'checkout-online-payment-options', label: 'Payment options', icon: 'fas fa-credit-card' },
          { id: 'checkout-terms-conditions', label: 'Terms & conditions', icon: 'fas fa-file-contract' },
        ],
      },
      {
        id: 'analytics-group',
        label: 'Analytics',
        icon: 'fas fa-chart-line',
        isGroup: true,
        children: [
          { id: 'analytics-advanced', label: 'Customer journey', icon: 'fas fa-chart-pie' },
          { id: 'analytics-booking-funnel', label: 'Booking funnel', icon: 'fas fa-filter' },
          { id: 'analytics-visitors', label: 'Visitors', icon: 'fas fa-globe' },
          { id: 'analytics-cancellations', label: 'Cancellations', icon: 'fas fa-ban' },
          { id: 'analytics-cookie-consent', label: 'Cookie consent', icon: 'fas fa-cookie-bite' },
        ],
      },
    ],
  },
  {
    sectionLabel: 'SYSTEM',
    items: [
      {
        id: 'acl-group',
        label: 'Users & roles',
        icon: 'fas fa-users-cog',
        isGroup: true,
        children: [
          { id: 'acl-all-users', label: 'All admin users', icon: 'fas fa-users' },
          { id: 'acl-locked-users', label: 'Locked users', icon: 'fas fa-user-lock' },
          { id: 'acl-user-roles', label: 'Roles & permissions', icon: 'fas fa-user-tag' },
          { id: 'acl-admin-access-log', label: 'Admin Access Log', icon: 'fas fa-clipboard-list' },
        ],
      },
      {
        id: 'acl-access-control',
        label: 'Access control',
        icon: 'fas fa-shield-alt',
        description: 'Roles and permissions',
        menuTargetId: 'acl-user-roles',
      },
      {
        id: 'system-group',
        label: 'System settings',
        icon: 'fas fa-cog',
        isGroup: true,
        children: [
          { id: 'settings', label: 'General settings', icon: 'fas fa-sliders-h' },
          { id: 'cron-history', label: 'Cron history', icon: 'fas fa-history', keywords: ['cron log', 'job history', 'scheduled runs'] },
          {
            id: 'email-settings',
            label: 'Email settings',
            icon: 'fas fa-envelope',
            keywords: ['smtp', 'email', 'mail server', 'smtp_host', 'outgoing mail'],
          },
          { id: 'loyalty-settings', label: 'Loyalty points', icon: 'fas fa-gift' },
          { id: 'chatbot-settings', label: 'Chatbot', icon: 'fas fa-comments' },
          { id: 'cancellation-settings', label: 'Cancellation', icon: 'fas fa-ban' },
          { id: 'maintenance-settings', label: 'Maintenance mode', icon: 'fas fa-tools' },
          { id: 'admin-ip-allowlist', label: 'Admin IP allowlist', icon: 'fas fa-network-wired' },
          { id: 'attack-protection-settings', label: 'Attack protection', icon: 'fas fa-shield-virus' },
          { id: 'active-sessions', label: 'Active sessions', icon: 'fas fa-user-clock' },
          { id: 'database-settings', label: 'Database', icon: 'fas fa-database' },
          { id: 'mobile-app-settings', label: 'Mobile app', icon: 'fas fa-mobile-alt' },
          { id: 'website-design-settings', label: 'Website design', icon: 'fas fa-palette' },
          { id: 'website-schema-settings', label: 'Schema.org', icon: 'fas fa-code' },
          { id: 'driver-app-settings', label: 'Driver app', icon: 'fas fa-car' },
          { id: 'status-management', label: 'Status management', icon: 'fas fa-code-branch' },
        ],
      },
      {
        id: 'notification-center',
        label: 'Notification center',
        icon: 'fas fa-bell',
        description: 'Header alerts and realtime event log',
      },
      {
        id: 'logs',
        label: 'Audit logs',
        icon: 'fas fa-clipboard-list',
        description: 'System and email logs',
      },
      {
        id: 'system-tools-group',
        label: 'Tools',
        icon: 'fas fa-wrench',
        isGroup: true,
        children: [
          { id: 'pricing-test', label: 'Booking test cases', icon: 'fas fa-vial' },
          { id: 'qa-test-results', label: 'QA test results', icon: 'fas fa-clipboard-check' },
          { id: 'api-docs', label: 'API docs', icon: 'fas fa-book' },
          { id: 'code-flow', label: 'Code Flow', icon: 'fas fa-project-diagram' },
          { id: 'dev-doc', label: 'Dev Doc', icon: 'fas fa-file-code' },
          { id: 'db-backup', label: 'DB Backup', icon: 'fas fa-database' },
          { id: 'cache-management', label: 'Cache Management', icon: 'fas fa-broom' },
        ],
      },
      {
        id: 'account-group',
        label: 'Account',
        icon: 'fas fa-user-cog',
        isGroup: true,
        children: [
          { id: 'profile-settings', label: 'Profile', icon: 'fas fa-user-circle' },
          { id: 'change-password', label: 'Change password', icon: 'fas fa-key' },
        ],
      },
    ],
  },
];

/** Flatten all menu ids for ACL fallback / ordering. */
function collectMenuIds(items, ids) {
  for (const item of items || []) {
    if (item.menuTargetId) {
      ids.push(item.menuTargetId);
    } else {
      ids.push(item.id);
    }
    if (item.isGroup && item.children?.length) {
      collectMenuIds(item.children, ids);
    }
  }
}

export function flattenSidebarMenuIds(sections = SIDEBAR_SECTIONS) {
  const ids = [];
  for (const section of sections) {
    collectMenuIds(section.items, ids);
  }
  return ids;
}
